/**
 * `GET`/`POST /consola/precio/politica` — la política de precio VERSIONADA que decide si la
 * plataforma se compromete o no con el precio de un viaje
 * (`apps/api/src/precio/consola-politica-precio.controller.ts`).
 *
 * 🔴 **El CATÁLOGO de modelos viene del BACKEND, no está duplicado acá — y es deliberado.** La
 * alternativa obvia (una lista de los ocho modelos escrita en la consola, con sus textos y su
 * estado de habilitación) crea DOS listas que envejecen por separado, y las dos formas de
 * divergencia son malas: la consola mostrando bloqueado algo que el backend ya habilitó (trabajo
 * hecho que nadie puede usar), o la consola ofreciendo publicar algo que el gate rechaza (y el
 * 400 sería la primera señal). El gate vive en un solo lugar y esta pantalla PREGUNTA.
 *
 * ⚠️ **Las FORMAS (`PoliticaEfectiva`, `VersionPolitica`, `FichaModelo`) son PROPIAS y no salen
 * de `dominio/contratos.ts`** — mismo criterio que `parametrosDispatchApi.ts`,
 * `verificacionApi.ts` e `incidentesApi.ts`: el backend devuelve `string | null` donde un tipo de
 * mock declararía `string`, y reusar el mock compilaría igual y rompería en runtime al renderizar
 * `null`.
 *
 * 🔑 **Pero ese argumento NO alcanza al VOCABULARIO, y por eso `ModeloDePrecio` y
 * `ParametroPrecio` sí vienen del puente.** Un `null` que el mock no declara es una diferencia de
 * FORMA; una unión de literales no tiene forma que pueda diferir — o los identificadores son los
 * mismos que los del backend, o el tipo está mal. Escribirlos acá a mano creaba una tercera lista
 * (con el enum de Prisma y con el catálogo del backend) que **nadie podía ver envejecer**: la
 * consola compila igual con la lista corta, así que un modelo nuevo llegaría del backend y esta
 * pantalla lo trataría como un valor que no existe. Ahora salen de `@taxi/contratos` por
 * `dominio/contratos.ts` —la regla del puente: ninguna pantalla importa del paquete directamente—
 * y `apps/api/src/contratos.conformidad.spec.ts` afirma que esa lista coincide con el enum de
 * Prisma, así que la divergencia rompe el build en vez de aparecer en pantalla.
 */
import type { ModeloDePrecio, ParametroPrecio } from './contratos';

import { obtener, postear } from './clienteApi';

/**
 * Se re-exportan para que las pantallas sigan tomando TODO el vocabulario de este endpoint de un
 * solo módulo (`Precio.tsx` y `precio.logica.ts` ya importaban de acá). Es un salto más sobre el
 * puente, no un atajo alrededor: el valor sale igual de `@taxi/contratos`.
 */
export type { ModeloDePrecio, ParametroPrecio };

/**
 * De dónde salió la política que los flujos de viaje están usando AHORA.
 *
 * 🔴 `'FALLBACK'` no es un detalle interno: significa que **ninguna versión está gobernando el
 * precio** —no hay política publicada, o la lectura falló— y que los viajes de ese rato quedan
 * sin `politicaPrecioId`, o sea no atribuibles a ninguna versión. Sin este campo, "hay una
 * política que dice rango referencial" y "no hay ninguna política" se verían idénticos.
 */
export type OrigenPolitica = 'BD' | 'FALLBACK';

/** Lo que los flujos usan ahora — la MISMA lectura que hace el puerto `LectorPoliticaPrecio`. */
export interface PoliticaEfectiva {
  /** `null` cuando `origen === 'FALLBACK'`: no hay fila, así que no hay qué atribuir. */
  id: string | null;
  version: number | null;
  modelo: ModeloDePrecio;
  ventanaPrecioSeg: number | null;
  maxOfertasPrecio: number | null;
  toleranciaTechoPct: number | null;
  origen: OrigenPolitica;
}

/** Una fila del historial. Fechas serializadas como ISO string sobre HTTP (`Date` → string). */
export interface VersionPolitica {
  id: string;
  version: number;
  vigenteDesde: string;
  /** `null` = es la versión VIGENTE. Es así como la pantalla la identifica. */
  vigenteHasta: string | null;
  modelo: ModeloDePrecio;
  ventanaPrecioSeg: number | null;
  maxOfertasPrecio: number | null;
  toleranciaTechoPct: number | null;
  /**
   * `null` cuando la versión no tiene autor registrado. **No se rellena con un centinela**: la
   * columna no distingue "nació con el sistema" de "alguien la escribió por fuera de la consola",
   * y ponerlo inventaría una atribución. Se muestra "sin registrar".
   */
  editadoPor: string | null;
  motivo: string | null;
}

/** La ficha de un modelo, tal como la publica el backend. Ver `apps/api/src/precio/modelos-precio.ts`. */
export interface FichaModelo {
  modelo: ModeloDePrecio;
  titulo: string;
  descripcion: string;
  /** Si se puede publicar HOY. `false` ⇒ el backend responde 400 citando `falta`. */
  publicable: boolean;
  /** Qué falta construir. `null` ⇔ `publicable === true`. */
  falta: string | null;
  /**
   * Advertencia de R-05, **distinta del gate**. Un texto acá significa que el modelo crea una
   * obligación que la plataforma sólo puede verificar por RECLAMO, nunca por observación.
   * **No bloquea:** se publica sabiéndolo, y por eso la pantalla la muestra ANTES de confirmar.
   */
  advertencia: string | null;
  /** Qué parámetros usa este modelo. Los que están son obligatorios; los que no, se rechazan. */
  parametros: readonly ParametroPrecio[];
}

export interface EstadoPolitica {
  efectivos: PoliticaEfectiva;
  /** Historial completo, la más nueva primero. Vacío si la tabla nunca se escribió. */
  historial: VersionPolitica[];
  catalogo: FichaModelo[];
}

/**
 * El cuerpo del `POST`. **Snapshot completo, no un parche.**
 *
 * ⚠️ Los tres parámetros son opcionales acá porque **cuáles van depende del modelo**, y mandar
 * uno que el modelo no usa es un 400 del backend (guardarlo dejaría en la tabla un número que no
 * gobierna nada). `armarPublicacion` en `Precio.tsx` es quien decide cuáles viajan.
 */
export interface PublicarPolitica {
  modelo: ModeloDePrecio;
  ventanaPrecioSeg?: number;
  maxOfertasPrecio?: number;
  toleranciaTechoPct?: number;
  /** Obligatorio, mínimo 10 caracteres — lo exige el backend (`@MinLength(10)`). */
  motivo: string;
}

export const obtenerPoliticaPrecio = (): Promise<EstadoPolitica> =>
  obtener<EstadoPolitica>('/consola/precio/politica');

/**
 * Publica una versión NUEVA. Nunca edita la vigente.
 *
 * Puede fallar con **400** (el modelo todavía no está habilitado por el gate, o los parámetros no
 * son los que el modelo usa) o con **409** si otra publicación se aplicó en simultáneo: en ese
 * caso hay que releer el estado antes de reintentar, porque el borrador se armó sobre una versión
 * que ya no es la vigente. `ErrorApi.message` trae el motivo concreto del backend.
 */
export const publicarPoliticaPrecio = (cuerpo: PublicarPolitica): Promise<VersionPolitica> =>
  postear<VersionPolitica>('/consola/precio/politica', cuerpo);
